/**
 * 拼图游戏主控制器
 * 门面模式：统一协调各个模块
 */
import { Vector2, HexCoord, PieceState, EdgeDirection, ComplexityLevel, Quadrant, ParticleEffectType, GameConfig, ThemeConfig, THEMES } from '../types';
import { PuzzleBoard } from './PuzzleBoard';
import { PuzzlePiece } from './PuzzlePiece';
import { PieceGroup } from './PieceGroup';
import { WebGLRenderer } from '../render/WebGLRenderer';
import { ParticleManager } from '../render/ParticleManager';
import { TextureAtlasPacker } from '../render/TextureAtlasPacker';
import { VisualDimmer } from '../render/VisualDimmer';
import { DragController } from '../input/DragController';
import { SaveManager } from '../storage/SaveManager';
import { UIManager } from '../ui/UIManager';
import { ImageUploader, PresetImage } from '../ui/ImageUploader';
import { LeaderboardManager } from '../ui/LeaderboardManager';
import { PocketManager } from './PocketManager';
import { i18n } from '../core/I18nManager';
import { ApiClient } from '../core/ApiClient';
import { HexMath } from '../core/HexMath';

export interface GameEvents {
    onGameStart?: () => void;
    onGameComplete?: (time: number) => void;
    onPieceSnapped?: () => void;
    onError?: (message: string) => void;
}

// 性能优化配置
export interface PerformanceConfig {
    useAtlasThreshold: number;      // 使用图集的碎片数量阈值
    atlasMaxSize: number;           // 图集最大尺寸
}

export class PuzzleGame {
    private config: GameConfig;
    private board: PuzzleBoard;
    private renderer: WebGLRenderer;
    private particleManager: ParticleManager | null = null;
    private dragController: DragController;
    private saveManager: SaveManager;
    private uiManager: UIManager;
    private imageUploader: ImageUploader | null = null;
    private leaderboardManager: LeaderboardManager;
    private pocketManager: PocketManager;

    // 拼图元数据
    private puzzleId: string | null = null;
    private puzzleName: string | null = null;

    // 性能优化相关
    private atlasPacker: TextureAtlasPacker | null = null;
    private performanceConfig: PerformanceConfig;

    // 视觉退让控制器
    private visualDimmer: VisualDimmer;

    private events: GameEvents = {};
    private moveCount: number = 0;
    private isInitialized: boolean = false;
    private currentImageData: string | null = null;
    private _tickerId: number | null = null;
    private lastWheelTime: number = 0;

    constructor(
        puzzleContainer: HTMLElement,
        config?: Partial<GameConfig>,
        performanceConfig?: Partial<PerformanceConfig>
    ) {
        // 默认配置
        this.config = {
            rings: 5,
            tabSize: 0.1,
            jitter: 0.04,
            seed: Math.floor(Math.random() * 10000),
            imageSize: 1024,
            enableWarp: false,
            enableRotation: true,
            ...config
        };

        // 性能优化配置
        this.performanceConfig = {
            useAtlasThreshold: 50,
            atlasMaxSize: 2048,
            ...performanceConfig
        };

        // 初始化各模块
        this.board = new PuzzleBoard(this.config);
        this.renderer = new WebGLRenderer(puzzleContainer);
        this.dragController = new DragController(this.board);
        this.saveManager = SaveManager.getInstance();
        this.uiManager = new UIManager();
        this.leaderboardManager = new LeaderboardManager();
        this.pocketManager = new PocketManager(this.board, this.renderer);
        this.visualDimmer = new VisualDimmer();

        this.pocketManager.setOnExtract((pieceId, screenPos) => {
            const logicPos = this.renderer.screenToLogic(screenPos);
            // 将碎片瞬移到鼠标位置，然后开始拖拽
            const piece = this.board.getPiece(pieceId);
            if (piece) {
                const group = this.board.getGroupByPiece(piece);
                const pieces = group ? group.getPieces() : [piece];
                
                // 计算当前中心到鼠标的偏移
                const currentCenter = piece.position;
                const delta = { x: logicPos.x - currentCenter.x, y: logicPos.y - currentCenter.y };
                
                this.board.movePiece(piece, delta);
                this.renderer.update(this.board.getAllPieces(), this.board.getGroups());
                
                // 1. 启动逻辑层拖拽
                this.dragController.startDrag(pieceId, screenPos);
                // 2. 启动渲染层手动拖拽监听
                this.renderer.startManualDrag(pieceId);
            }
        });

        this.setupEventBindings();
    }

    /**
     * 设置事件监听
     */
    setEvents(events: GameEvents): void {
        this.events = { ...this.events, ...events };
    }

    /**
     * 切换主题
     */
    setTheme(theme: ThemeConfig): void {
        this.renderer.setTheme(theme);
    }

    /**
     * 设置事件绑定
     */
    private setupEventBindings(): void {
        // 拼图板事件
        this.board.setEvents({
            onPieceSnapped: (piece, result) => {
                this.moveCount++;
                this.uiManager.updateMoveCount(this.moveCount);

                // 播放吸附效果
                if (this.particleManager && result.snapPosition) {
                    const screenPos = this.renderer.logicToScreen(result.snapPosition);
                    this.particleManager.play(
                        ParticleEffectType.SnapSuccess,
                        screenPos
                    );
                }

                // 注意：不再需要手动调用 updatePieceOrGroupRender，
                // 因为游戏循环会根据 dirty 标记自动更新渲染

                this.renderer.highlightPiece(piece.id);
                this.events.onPieceSnapped?.();
            },

            onPiecePlaced: (piece) => {
                this.renderer.hideBaseMapPiece(piece.id);

                if (this.particleManager) {
                    const screenPos = this.renderer.logicToScreen(piece.position);
                    this.particleManager.play(
                        ParticleEffectType.PieceComplete,
                        screenPos
                    );
                }

                this.uiManager.updateProgress(
                    this.board.getPlacedCount(),
                    this.board.getTotalCount()
                );
            },

            onPieceUnplaced: (piece) => {
                this.renderer.showBaseMapPiece(piece.id);
                // 恢复阴影效果（syncPieceState 会根据 isPlaced 状态设置 idle 阴影）
                this.renderer.syncPieceState(piece);
                
                this.uiManager.updateProgress(
                    this.board.getPlacedCount(),
                    this.board.getTotalCount()
                );
            },

            onGroupCreated: (group, piece1, piece2, direction) => {
                const edgeType = piece1.edgeTypes.get(direction);
                const order = edgeType === 'tab' ? 'piece1Above' : 'piece2Above';

                console.log(`[PuzzleGame] onGroupCreated: group=${group.id}, dir=${direction}, edgeType=${edgeType}, order=${order}`);
                console.log(`[PuzzleGame]   piece1=${piece1.id}(${piece1.coord.u},${piece1.coord.v}) pos=(${piece1.position.x.toFixed(1)},${piece1.position.y.toFixed(1)})`);
                console.log(`[PuzzleGame]   piece2=${piece2.id}(${piece2.coord.u},${piece2.coord.v}) pos=(${piece2.position.x.toFixed(1)},${piece2.position.y.toFixed(1)})`);
                console.log(`[PuzzleGame]   group.center=(${group.center.x.toFixed(1)},${group.center.y.toFixed(1)}), rot=${group.rotation}`);

                this.renderer.createGroupContainer(
                    group.id,
                    piece1.id,
                    piece2.id,
                    group.center,
                    group.rotation,
                    piece1.position,
                    piece2.position,
                    order
                );

                this.renderer.disablePieceInteraction(piece1.id);
                this.renderer.disablePieceInteraction(piece2.id);

                // 播放金色轮廓闪烁特效
                this.renderer.highlightPiece(piece1.id);
                this.renderer.highlightPiece(piece2.id);
                
                // 播放少量粒子
                if (this.particleManager) {
                    const center = {
                        x: (piece1.position.x + piece2.position.x) / 2,
                        y: (piece1.position.y + piece2.position.y) / 2
                    };
                    const screenCenter = this.renderer.logicToScreen(center);
                    this.particleManager.play(ParticleEffectType.SnapSuccess, screenCenter, { count: 5, size: 2 });
                }
            },

            onGroupMerged: (survivingGroup, mergedGroup) => {
                const piecePositions: Record<string, Vector2> = {};
                survivingGroup.getPieces().forEach(p => {
                    piecePositions[p.id] = p.position;
                });

                this.renderer.mergeGroupContainers(
                    survivingGroup.id,
                    mergedGroup.id,
                    survivingGroup.center,
                    survivingGroup.rotation,
                    piecePositions
                );
            },

            onPieceJoinedGroup: (piece, group, targetPiece, direction) => {
                // 判断谁才是真正"新加入"组的那个碎片
                // 如果主动拖拽的 piece 之前没有组，说明是 piece 加入了 targetPiece 的组
                // 如果 piece 之前就有组，说明是 targetPiece 加入了 piece 的组
                const joiningPiece = this.renderer.getPieceGroupId(piece.id) ? targetPiece : piece;
                const basePiece = joiningPiece === piece ? targetPiece : piece;

                // 修复: direction 可能来自组内其他成员而非 piece，所以不能用 piece.edgeTypes.get(direction)。
                // 改用 targetPiece 的反向边类型（targetPiece 始终是吸附匹配的实际碎片，方向始终正确）。
                const oppositeDir = HexMath.getOppositeDirection(direction);
                const targetEdgeType = targetPiece.edgeTypes.get(oppositeDir);
                let order: 'above' | 'below';
                if (joiningPiece === targetPiece) {
                    // targetPiece 是新加入者，其 tab/blank 直接决定层级
                    order = targetEdgeType === 'tab' ? 'above' : 'below';
                } else {
                    // piece（拖拽碎片，独立）是新加入者，target 有 tab 则 target 在上，joining 在下
                    order = targetEdgeType === 'tab' ? 'below' : 'above';
                }

                console.log(`[PuzzleGame] onPieceJoinedGroup: dir=${direction}, oppositeDir=${oppositeDir}, targetEdgeType=${targetEdgeType}, order=${order}`);
                console.log(`[PuzzleGame]   piece=${piece.id}(${piece.coord.u},${piece.coord.v}) pos=(${piece.position.x.toFixed(1)},${piece.position.y.toFixed(1)}) rendererGroup=${this.renderer.getPieceGroupId(piece.id)}`);
                console.log(`[PuzzleGame]   target=${targetPiece.id}(${targetPiece.coord.u},${targetPiece.coord.v}) pos=(${targetPiece.position.x.toFixed(1)},${targetPiece.position.y.toFixed(1)}) rendererGroup=${this.renderer.getPieceGroupId(targetPiece.id)}`);
                console.log(`[PuzzleGame]   joining=${joiningPiece.id}, base=${basePiece.id}, group.center=(${group.center.x.toFixed(1)},${group.center.y.toFixed(1)}), rot=${group.rotation}`);
                // 打印组内所有碎片位置
                for (const p of group.getPieces()) {
                    console.log(`[PuzzleGame]   groupPiece: ${p.id}(${p.coord.u},${p.coord.v}) pos=(${p.position.x.toFixed(1)},${p.position.y.toFixed(1)}) correct=(${p.correctPosition.x.toFixed(1)},${p.correctPosition.y.toFixed(1)})`);
                }

                this.renderer.addPieceToGroupContainer(
                    group.id,
                    joiningPiece.id,
                    group.center,
                    group.rotation,
                    basePiece.id,
                    order,
                    joiningPiece.position
                );

                // 必须禁用新加入碎片的独立交互，使其冒泡给组容器
                this.renderer.disablePieceInteraction(joiningPiece.id);

                // 播放金色轮廓闪烁特效
                this.renderer.highlightPiece(piece.id);
                this.renderer.highlightPiece(targetPiece.id);

                // 播放少量粒子
                if (this.particleManager) {
                    const center = {
                        x: (piece.position.x + targetPiece.position.x) / 2,
                        y: (piece.position.y + targetPiece.position.y) / 2
                    };
                    const screenCenter = this.renderer.logicToScreen(center);
                    this.particleManager.play(ParticleEffectType.SnapSuccess, screenCenter, { count: 5, size: 2 });
                }
            },

            onGameComplete: () => {
                this.uiManager.stopTimer();
                const time = this.uiManager.getElapsedTime();
                const difficulty = this.getDifficultyString();
                const pieceCount = this.board.getTotalCount();

                // 先弹出弹窗（本地数据立即展示）
                const { updateScore, showLoginHint, hideLoginHint } = this.uiManager.showCompleteDialog({
                    time,
                    difficulty,
                    pieceCount,
                    puzzleId: this.puzzleId || undefined,
                    puzzleName: this.puzzleName || undefined
                });
                this.events.onGameComplete?.(time);

                // 提交成绩的函数（登录后可复用）
                const submitScore = () => {
                    if (!this.puzzleId) return;
                    ApiClient.post<{ score: number; rank: number; isNewBest: boolean }>('/api/score/submit', {
                        puzzleId: this.puzzleId,
                        difficulty,
                        timeMs: time,
                        moves: 0
                    }, { skipErrorToast: true })
                    .then((result) => {
                        if (result) {
                            hideLoginHint();
                            updateScore(result.score, result.rank, result.isNewBest);
                        }
                        if (this.puzzleId) {
                            this.leaderboardManager.refresh(this.puzzleId, difficulty);
                        }
                    })
                    .catch(err => {
                        console.error('Failed to submit record', err);
                        // 未登录，显示登录提示
                        showLoginHint();
                        // 监听登录成功事件，自动重新提交
                        const onAuthChanged = (e: any) => {
                            if (e.detail.isLoggedIn) {
                                window.removeEventListener('auth-status-changed', onAuthChanged);
                                submitScore();
                            }
                        };
                        window.addEventListener('auth-status-changed', onAuthChanged);
                    });
                };

                // Submit record to backend
                if (this.puzzleId) {
                    submitScore();
                }

                if (this.particleManager) {
                    this.particleManager.playFireworks(
                        this.config.imageSize / 2,
                        this.config.imageSize / 2
                    );
                }

                // --- 逻辑补全：拼图完成后删除该拼图的自动存档 ---
                this.saveManager.delete('auto');
            }
        });

        // 渲染器事件
        this.renderer.setEvents({
            onPieceDragStart: (pieceId, position) => {
                this.dragController.startDrag(pieceId, position);
                const sameRegionIds = this.board.getSameRegionPieces(pieceId);
                const allSprites = this.renderer.getAllPieceSprites();
                this.visualDimmer.startDim(pieceId, sameRegionIds, allSprites);

                // 拖拽时启用口袋穿透
                this.pocketManager.setPassthrough(true);
            },

            onPieceDragMove: (pieceId, position) => {
                this.dragController.moveDrag(position);
            },

            onPieceDragEnd: (pieceId, position) => {
                // 恢复口袋交互
                this.pocketManager.setPassthrough(false);

                // 检查落点：按钮或展开的侧边栏
                const btnRect = this.pocketManager.getBtnRect();
                const panelRect = this.pocketManager.getPanelRect();
                const screenPos = this.renderer.logicToScreen(position);
                
                const isOverBtn = screenPos.x >= btnRect.left && screenPos.x <= btnRect.right &&
                                 screenPos.y >= btnRect.top && screenPos.y <= btnRect.bottom;
                
                const isPanelOpen = !document.getElementById('pocket-panel')?.classList.contains('translate-x-full');
                const isOverPanel = isPanelOpen && 
                                   screenPos.x >= panelRect.left && screenPos.x <= panelRect.right &&
                                   screenPos.y >= panelRect.top && screenPos.y <= panelRect.bottom;

                if (isOverBtn || isOverPanel) {
                    this.pocketManager.addItem(pieceId);
                    this.dragController.cancelDrag();
                } else {
                    this.dragController.endDrag(position);
                }

                const allSprites = this.renderer.getAllPieceSprites();
                this.visualDimmer.endDim(pieceId, allSprites);
            },

            onPieceRightClick: (pieceId, position) => {
                this.dragController.rotateAt(position, true);
            },

            onPieceDoubleTap: (pieceId, position) => {
                this.dragController.rotateAt(position, true);
            },

            onPieceLongPress: (pieceId, position) => {
                this.dragController.rotateAt(position, false);
            },

            onWheel: (position, deltaY) => {
                const now = Date.now();
                // Cooldown to prevent rapid firing (which looks like erratic rotation)
                if (now - this.lastWheelTime < 100) return;
                
                // Threshold to ignore minor trackpad jitters
                if (Math.abs(deltaY) < 10) return;

                this.lastWheelTime = now;
                this.dragController.rotateAt(position, deltaY > 0);
            },

            onKeyDown: (key) => {
                switch (key.toLowerCase()) {
                    case 'r': this.dragController.rotateCurrent(true); break;
                    case 'e': this.dragController.rotateCurrent(false); break;
                }
            }
        });

        // UI 管理器事件
        this.uiManager.setEvents({
            onConfigChange: (config) => this.updateConfig(config),
            onShuffle: () => this.shuffle(),
            onOrganize: () => this.organize(),
            onSave: () => this.save(),
            onLoad: (slotId) => this.load(slotId),
            onNewGame: () => window.location.href = '/',
            onUndo: () => this.dragController.undo(),
            onThemeChange: (themeName) => {
                const theme = THEMES[themeName];
                if (theme) {
                    this.setTheme(theme);
                }
            }
        });
    }

    /**
     * 初始化游戏
     */
    async initialize(): Promise<void> {
        try {
            await this.renderer.initialize();
            this.particleManager = new ParticleManager(
                this.renderer.getEffectsLayer(),
                this.renderer.getApp()
            );
            this.isInitialized = true;
            this.startTicker();
        } catch (error) {
            this.events.onError?.(`${i18n.t('msg_init_failed')}: ${error}`);
            throw error;
        }
    }

    /**
     * 启动游戏循环
     */
    private startTicker(): void {
        if (this._tickerId !== null) return;
        const tick = () => {
            this.update();
            this._tickerId = requestAnimationFrame(tick);
        };
        this._tickerId = requestAnimationFrame(tick);
    }

    /**
     * 停止游戏循环
     */
    private stopTicker(): void {
        if (this._tickerId !== null) {
            cancelAnimationFrame(this._tickerId);
            this._tickerId = null;
        }
    }

    /**
     * 每帧更新
     */
    private update(): void {
        this.renderer.update(this.board.getAllPieces(), this.board.getGroups());
    }

    /**
     * 开始新游戏
     */
    async startGame(imageSource: string | File, puzzleId?: string, puzzleName?: string): Promise<void> {
        if (!this.isInitialized) {
            await this.initialize();
        }

        try {
            this.puzzleId = puzzleId || null;
            this.puzzleName = puzzleName || null;
            this.renderer.clearPieces();
            this.renderer.clearBaseMap();
            this.renderer.clearAtlas();

            // 设置拼图逻辑尺寸，触发缩放因子计算
            this.renderer.setPuzzleSize(this.config.imageSize);

            await this.board.initialize(imageSource);

            if (typeof imageSource === 'string') {
                this.currentImageData = imageSource;
            } else {
                this.currentImageData = await this.fileToBase64(imageSource);
            }

            // Determine difficulty string
            const difficulty = this.getDifficultyString();

            // Refresh leaderboard if we have a puzzle ID
            if (this.puzzleId) {
                this.leaderboardManager.refresh(this.puzzleId, difficulty);
                // 报告游戏开始，更新热度统计
                ApiClient.post('/api/game/start', { puzzleId: this.puzzleId }, { skipErrorToast: true }).catch(() => {});
            }

            const totalPieces = this.board.getTotalCount();
            const allPieces = this.board.getAllPieces();
            const allSlicedPieces = allPieces.map(p => this.board.getSlicedPiece(p.id)!).filter(Boolean);

            console.log(`游戏启动: 总碎片数=${totalPieces}, 已切割碎片数=${allSlicedPieces.length}`);

            if (totalPieces >= this.performanceConfig.useAtlasThreshold) {
                this.setupAtlasMode(allSlicedPieces);
            }

            this.renderer.createBaseMap(allSlicedPieces);

            if (this.renderer.isUsingAtlas()) {
                this.renderer.addPiecesFromAtlas(allPieces, allSlicedPieces);
            } else {
                for (let i = 0; i < allPieces.length; i++) {
                    const piece = allPieces[i];
                    const sliced = this.board.getSlicedPiece(piece.id);
                    if (sliced) {
                        this.renderer.addPieceSprite(piece, sliced, i);
                    }
                }
            }
            this.shuffle();
            this.moveCount = 0;
            this.dragController.clearHistory();
            this.pocketManager.clear();

            this.uiManager.updateProgress(0, totalPieces);
            this.uiManager.updateMoveCount(0);
            this.uiManager.startTimer();

            this.events.onGameStart?.();
        } catch (error) {
            this.events.onError?.(`${i18n.t('msg_start_game_failed')}: ${error}`);
            throw error;
        }
    }

    private setupAtlasMode(slicedPieces: any[]): void {
        this.atlasPacker = new TextureAtlasPacker({
            maxSize: this.performanceConfig.atlasMaxSize,
            padding: 2
        });

        const packData = slicedPieces.map(piece => ({
            id: `${piece.coord.u}_${piece.coord.v}`,
            texture: piece.texture
        }));
        this.atlasPacker.packAll(packData);
        this.renderer.setAtlas(this.atlasPacker);
    }

    shuffle(): void {
        this.renderer.dissolveGroups();
        const rendererSize = this.renderer.getSize();
        const padding = this.renderer.getPadding();
        
        // 计算中间“工作区”的屏幕逻辑边界
        // 排除顶栏 (padding.top) 和底栏 (padding.bottom)
        const topLeft = this.renderer.screenToLogic({ x: 0, y: padding.top });
        const bottomRight = this.renderer.screenToLogic({ 
            x: rendererSize.width, 
            y: rendererSize.height - padding.bottom 
        });
        
        const screenBounds = {
            minX: topLeft.x,
            maxX: bottomRight.x,
            minY: topLeft.y,
            maxY: bottomRight.y
        };

        this.board.shuffle(this.config.imageSize, this.config.imageSize, screenBounds);
    }

    organize(): void {
        this.board.organize();
    }

    save(slotId?: string): boolean {
        const id = slotId || `save_${Date.now()}`;
        const success = this.saveManager.save(
            id,
            this.config,
            this.board.exportPieceStates(),
            this.uiManager.getElapsedTime(),
            this.moveCount,
            this.currentImageData || undefined,
            this.puzzleId || undefined,
            this.puzzleName || undefined
        );

        if (success) {
            this.uiManager.showMessage(i18n.t('msg_game_saved'), 'success');
        } else {
            this.uiManager.showMessage(i18n.t('msg_save_failed'), 'error');
        }
        return success;
    }

    async load(slotId: string): Promise<boolean> {
        const save = this.saveManager.load(slotId);
        if (!save) {
            this.uiManager.showMessage(i18n.t('msg_load_failed'), 'error');
            return false;
        }

        try {
            this.config = save.config;
            this.uiManager.updateConfigDisplay(this.config);

            if (save.imageData) {
                await this.startGame(save.imageData);
                this.board.importPieceStates(save.pieces);
                
                // 恢复口袋状态
                this.pocketManager.clear();
                this.board.getAllPieces().forEach(p => {
                    if (p.isInPocket) {
                        this.pocketManager.restoreItem(p.id);
                    }
                });

                // 同步渲染器状态（归位阴影、底图显示）
                this.renderer.syncAllPieceStates(this.board.getAllPieces());

                this.moveCount = save.moveCount;
                this.uiManager.startTimer(save.elapsedTime);
                this.uiManager.updateMoveCount(this.moveCount);
                this.uiManager.updateProgress(
                    this.board.getPlacedCount(),
                    this.board.getTotalCount()
                );
                this.uiManager.showMessage(i18n.t('msg_game_loaded'), 'success');
                return true;
            }
        } catch (error) {
            this.events.onError?.(`${i18n.t('msg_load_failed')}: ${error}`);
        }
        return false;
    }

    autoSave(): boolean {
        return this.saveManager.autoSave(
            this.config,
            this.board.exportPieceStates(),
            this.uiManager.getElapsedTime(),
            this.moveCount,
            this.currentImageData || undefined,
            this.puzzleId || undefined,
            this.puzzleName || undefined
        );
    }

    updateConfig(config: Partial<GameConfig>): void {
        this.config = { ...this.config, ...config };
        this.board.updateConfig(this.config);

        // 同步旋转设置
        if (config.enableRotation !== undefined) {
            this.board.getSmartLayoutManager().setEnableRandomRotation(config.enableRotation);
            this.dragController.setEnableRotation(config.enableRotation);
        }
    }

    setImageUploader(container: HTMLElement, presetImages?: PresetImage[]): void {
        this.imageUploader = new ImageUploader(container, {
            presetImages: presetImages || []
        });

        this.imageUploader.setEvents({
            onImageSelected: async (source) => {
                await this.startGame(source);
                this.imageUploader?.hide();
            },
            onError: (message) => {
                this.uiManager.showMessage(message, 'error');
            }
        });
    }

    showImageSelector(): void {
        this.imageUploader?.show();
    }

    setUIElements(elements: any): void {
        this.uiManager.initialize(elements);
        this.uiManager.updateConfigDisplay(this.config);
    }

    setLeaderboardContainer(container: HTMLElement): void {
        this.leaderboardManager.setContainer(container);
    }

    private getDifficultyString(): string {
        const count = this.board.getTotalCount();
        if (count > 300) return 'hard';
        if (count > 100) return 'medium';
        return 'easy';
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    getGameState() {
        return {
            isComplete: this.board.isComplete,
            placedCount: this.board.getPlacedCount(),
            totalCount: this.board.getTotalCount(),
            elapsedTime: this.uiManager.getElapsedTime(),
            moveCount: this.moveCount
        };
    }

    pause(): void {
        this.uiManager.pauseTimer();
        this.stopTicker();
    }

    resume(): void {
        this.uiManager.resumeTimer();
        this.startTicker();
    }

    getDebugInfo() {
        return {
            totalPieces: this.board.getTotalCount(),
            useAtlas: this.renderer.isUsingAtlas(),
            atlasCount: this.renderer.getAtlasCount(),
            atlasThreshold: this.performanceConfig.useAtlasThreshold
        };
    }

    setShowPieceNumber(show: boolean): void {
        this.board.getImageSlicer().setShowPieceNumber(show);
    }

    setEnableRandomRotation(enable: boolean): void {
        this.board.getSmartLayoutManager().setEnableRandomRotation(enable);
    }

    setBaseMapVisible(visible: boolean): void {
        this.renderer.setBaseMapVisible(visible);
    }

    toggleGuideImage(visible: boolean): void {
        this.renderer.setGuideImage(this.currentImageData, visible, 1.0);
    }

    toggleDebugNumbers(show?: boolean): void {
        this.renderer.toggleDebugNumbers(show);
    }

    getCurrentImageSource(): string | null {
        return this.currentImageData;
    }

    destroy(): void {
        this.stopTicker();
        this.autoSave();
        this.board.getImageSlicer().destroy();
        this.particleManager?.destroy();
        this.renderer.destroy();
        this.dragController.destroy();
        this.uiManager.destroy();
        this.imageUploader?.destroy();
        this.atlasPacker?.clear();
        this.visualDimmer.destroy();
    }
}
